/**
 * Date formatting utilities for consistent timezone handling.
 * All dates from the API are assumed to be UTC and displayed in browser's local timezone.
 */

/**
 * Parse a date input (Date object or ISO string) and ensure it's correctly interpreted.
 * Dates without timezone suffix are assumed to be UTC.
 */
export function parseDate(dateInput: Date | string | null | undefined): Date | null {
  if (!dateInput) return null;

  if (dateInput instanceof Date) {
    return dateInput;
  }

  // If the string doesn't have a timezone indicator, append Z to interpret as UTC
  const dateStr = dateInput.includes('Z') || dateInput.includes('+') || /\d{2}:\d{2}:\d{2}[+-]/.test(dateInput)
    ? dateInput
    : dateInput + 'Z';

  return new Date(dateStr);
}

/**
 * Format a date as relative time (e.g., "just now", "5m ago", "2h ago")
 */
export function formatRelativeTime(dateInput: Date | string | null | undefined): string {
  const date = parseDate(dateInput);
  if (!date) return '-';

  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < 0) {
    // Future date
    return 'in the future';
  } else if (diff < 60000) {
    return 'just now';
  } else if (diff < 3600000) {
    const minutes = Math.floor(diff / 60000);
    return `${minutes}m ago`;
  } else if (diff < 86400000) {
    const hours = Math.floor(diff / 3600000);
    return `${hours}h ago`;
  } else if (diff < 86400000 * 30) {
    const days = Math.floor(diff / 86400000);
    return `${days}d ago`;
  } else {
    return formatDate(date);
  }
}

/**
 * Format a date as relative day (e.g., "Today", "Yesterday", "3 days ago")
 */
export function formatRelativeDay(dateInput: Date | string | null | undefined): string {
  const date = parseDate(dateInput);
  if (!date) return 'Never';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 30) return `${diffDays} days ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}

/**
 * Format a date in localized short format (e.g., "Jan 15, 2024")
 */
export function formatDate(dateInput: Date | string | null | undefined): string {
  const date = parseDate(dateInput);
  if (!date) return '-';

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a time in localized format (e.g., "10:30 AM")
 */
export function formatTime(dateInput: Date | string | null | undefined): string {
  const date = parseDate(dateInput);
  if (!date) return '-';

  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format a date and time in localized format (e.g., "Jan 15, 2024, 10:30 AM")
 */
export function formatDateTime(dateInput: Date | string | null | undefined): string {
  const date = parseDate(dateInput);
  if (!date) return '-';

  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format a duration between two dates (e.g., "2h 15m 30s")
 */
export function formatDuration(
  startInput: Date | string | null | undefined,
  endInput: Date | string | null | undefined
): string {
  const start = parseDate(startInput);
  const end = parseDate(endInput);

  if (!start || !end) return '-';

  const durationMs = end.getTime() - start.getTime();
  if (durationMs < 0) return '-';

  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}

/**
 * Format a duration in short format (e.g., "2h 15m" instead of "2h 15m 30s")
 */
export function formatDurationShort(
  startInput: Date | string | null | undefined,
  endInput: Date | string | null | undefined
): string | null {
  const start = parseDate(startInput);
  const end = parseDate(endInput);

  if (!start || !end) return null;

  const durationMs = end.getTime() - start.getTime();
  if (durationMs < 0) return null;

  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}

/**
 * Check if a date is expired (in the past)
 */
export function isExpired(dateInput: Date | string | null | undefined): boolean {
  const date = parseDate(dateInput);
  if (!date) return false;
  return date.getTime() < Date.now();
}

/**
 * Format uptime from seconds (e.g., "5d 2h 30m")
 */
export function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);

  return parts.join(' ');
}
